<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
  import {computed, ref, onBeforeMount, onUnmounted, useAttrs} from 'vue';
  import {getComponentConfig} from '@core/utils/getComponentConfig';
  import ListItemTitle from './ListItemTitle.vue';

  defineOptions({inheritAttrs: false});

  interface Props {
    title?: string;
    to?: string | object;
    href?: string;
    active?: boolean;
    disabled?: boolean;
    density?: 'default' | 'compact';
    class?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    title: undefined,
    to: undefined,
    href: undefined,
    active: false,
    disabled: false,
    density: 'default',
    class: '',
  });

  const defaultConfig = {
    base: 'flex items-center gap-4 px-4 transition-colors duration-200 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50',
    densities: {
      default: 'py-3',
      compact: 'py-1.5',
    },
    active: 'bg-brand-primary/5 text-brand-primary',
    disabled: 'opacity-50 cursor-not-allowed pointer-events-none',
  };

  const config = ref(defaultConfig);

  let configLoadCancelled = false;
  onUnmounted(() => {
    configLoadCancelled = true;
  });

  onBeforeMount(async () => {
    try {
      const next = await getComponentConfig('ListItem', defaultConfig);
      if (!configLoadCancelled) {
        config.value = next;
      }
    } catch {
      if (!configLoadCancelled) {
        config.value = defaultConfig;
      }
    }
  });

  const attrs = useAttrs();

  const componentTag = computed(() => {
    if (props.to) return 'router-link';
    if (props.href) return 'a';
    return 'div';
  });

  /** When the root is a plain `div` with a click handler, expose button semantics for WCAG 2.1.1. */
  const mergedRootAttrs = computed(() => {
    const raw = attrs as Record<string, unknown>;
    if (componentTag.value !== 'div' || props.disabled) {
      return raw;
    }
    const onClick = raw.onClick;
    if (typeof onClick !== 'function') {
      return raw;
    }
    const userKeydown = raw.onKeydown;
    return {
      ...raw,
      role: 'button',
      tabindex: 'tabindex' in raw ? raw.tabindex : 0,
      onKeydown: (e: KeyboardEvent) => {
        if (typeof userKeydown === 'function') {
          (userKeydown as (ev: KeyboardEvent) => void)(e);
        }
        if (e.defaultPrevented) {
          return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          (onClick as (ev: Event) => void)(e);
        }
      },
    };
  });

  const itemClasses = computed(() => [
    'list__item',
    config.value.base,
    config.value.densities[props.density] || config.value.densities.default,
    props.active ? config.value.active : '',
    props.disabled ? config.value.disabled : '',
    props.class,
  ]);
</script>

<template>
  <component
    :is="componentTag"
    :to="to"
    :href="href"
    :class="itemClasses"
    v-bind="mergedRootAttrs">
    <div
      v-if="$slots.prepend"
      class="list__item-prepend shrink-0">
      <slot name="prepend" />
    </div>

    <div class="list__item-body grow min-w-0">
      <ListItemTitle v-if="title">
        {{ title }}
      </ListItemTitle>
      <slot />
    </div>

    <div
      v-if="$slots.append"
      class="list__item-append shrink-0">
      <slot name="append" />
    </div>
  </component>
</template>
